import { getTotalUnpaidPaymentsAmount } from '../../../pure-js/libs/EnrollmentHelper'
import {
  Activity,
  Client,
  EnrollmentPaymentStatus,
  EnrollmentWithPayment,
  PayFor
} from '../../../pure-js/types/GrooverTypes'
import { isActivityFree } from '../utils/activityUtils'

/**
 * Whose balance the QR overlay speaks for.
 *
 * A personal registration link carries `?u=<enrollmentUserId>` (see
 * `UrlHelper.getEnrollmentUrlForEnrollmentUser`), and that participant only owes their own
 * share — so a partner who has already paid must not be told "payment pending" just because
 * the main user still owes. Mirrors `effectivePayFor` in `CourseRegistrationStepFour`.
 */
export const resolvePayForForEnrollmentUser = (
  enrollment: EnrollmentWithPayment | undefined,
  enrollmentUserId: string | undefined
): PayFor => {
  if (!enrollmentUserId || !enrollment) return PayFor.Both
  if (enrollment.partnerUser?.id === enrollmentUserId) return PayFor.Partner
  if (enrollment.mainUser?.id === enrollmentUserId) return PayFor.Main
  return PayFor.Both
}

/** The fields getToBePaidData walks unguarded. */
const hasAmountsToEvaluate = (enrollment: EnrollmentWithPayment): boolean =>
  Array.isArray(enrollment.priceDetails) && Array.isArray(enrollment.payments) && !!enrollment.mainUser

export type QrPaymentPendingBadgeParams = {
  activity?: Activity
  /** Activities the price is derived from: the enrollment's own when present, else the selection. */
  activitiesForPricing: Activity[]
  /** Payment status held in register state (defaults to Unpaid before anything loads). */
  statePaymentStatus: EnrollmentPaymentStatus
  enrollment?: EnrollmentWithPayment
  /** `?u=` from the URL, when the link is scoped to one participant. */
  enrollmentUserId?: string
  /** Needed for the administration-fee rule; without it a stored fee counts even for clients that disabled it. */
  client?: Client
}

/**
 * Whether the QR overlay should carry the "Payment pending" badge.
 *
 * Registrations are always stored `paymentStatus: Unpaid` (see
 * `ProcessHandlerRegistration.createEnrollment`) and nothing ever moves a free one to `Paid`,
 * because no payment happens — so `paymentStatus` alone labelled free registrations
 * "Payment pending" forever. The badge belongs to an actual outstanding balance: hide it when
 * the activity is free, and when this participant has nothing left to pay (a 100% discount, or
 * an already-settled share).
 */
export const shouldShowQrPaymentPendingBadge = ({
  activity,
  activitiesForPricing,
  statePaymentStatus,
  enrollment,
  enrollmentUserId,
  client
}: QrPaymentPendingBadgeParams): boolean => {
  if (statePaymentStatus !== EnrollmentPaymentStatus.Unpaid) return false
  if (
    enrollment?.paymentStatus === EnrollmentPaymentStatus.Paid ||
    enrollment?.paymentStatus === EnrollmentPaymentStatus.PartlyPaid
  ) {
    return false
  }

  if (activity && isActivityFree(activity, activitiesForPricing)) return false

  // Only trustworthy once the enrollment is loaded; before that fall back to paymentStatus alone.
  // The shape check is deliberate: getToBePaidData reads priceDetails/payments/mainUser without
  // guarding, and this runs during render, so a partial payload would blank the page.
  if (enrollment?.id && hasAmountsToEvaluate(enrollment)) {
    const payFor = resolvePayForForEnrollmentUser(enrollment, enrollmentUserId)
    // getTotalUnpaidPaymentsAmount rather than getToBePaidData: the latter cannot forward `client`,
    // and without it a stored administration fee is counted even for clients that disabled the fee.
    // Not getUserPaymentStatus either — that one only sums the Main/Partner slices and reports
    // due = 0 for PayFor.Both, which would hide the badge on every unscoped registration.
    // Admin fee included: when it does apply, it is money the participant still has to pay.
    if (getTotalUnpaidPaymentsAmount(enrollment, payFor, true, client) <= 0) return false
  }

  return true
}
